import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  useReveal, ServiceHero, StatsRow, ProcessSection, WhatWeDeliver,
  ServiceCaseStudies, ServiceFAQ,
  IndustriesSection, WhyZoneX, ServiceCTA, Footer,
} from './shared/ServicePageSections';

const COLOR = '#003CFF';

const PROCESS = [
  {
    num:'01', title:'Understand Your Product & Goals',
    desc:'Every great IoT product begins with clarity. We work with you to understand the business problem you are solving, the end-user experience you are building for, and the technical constraints you are working within — before a single design decision is made.',
    points:[
      'Business goal, market context, and user persona definition',
      'Product requirements specification and use-case mapping',
      'Technical constraints review — power, connectivity, form factor',
      'Risk identification and feasibility assessment',
    ],
  },
  {
    num:'02', title:'Research, Plan & Architect',
    desc:'We study your use case deeply, research available technologies, and design a complete system architecture — hardware, firmware, cloud, and application layers — before development begins. The goal is a clear, costed roadmap that aligns engineering with product reality.',
    points:[
      'Technology stack evaluation and platform selection',
      'System architecture design across all product layers',
      'Component and supplier shortlisting with cost model',
      'Realistic project roadmap and milestone plan',
    ],
  },
  {
    num:'03', title:'Design & Engineer',
    desc:'Our engineers work across hardware, embedded firmware, cloud backend, and user applications simultaneously — as one integrated team. No handoffs, no silos, no integration surprises. Every layer is designed to work with every other layer from day one.',
    points:[
      'PCB schematic design, layout, and prototype fabrication',
      'Embedded firmware with RTOS, drivers, and connectivity stack',
      'Cloud backend, IoT data pipeline, and REST API development',
      'Web dashboard and mobile app development',
    ],
  },
  {
    num:'04', title:'Test & Validate',
    desc:'We test every layer thoroughly — not just in the lab but against real-world conditions: weak signals, power interruptions, boundary data values, and field environments. We fix problems before they become field failures.',
    points:[
      'Unit, integration, and end-to-end system testing',
      'Environmental, connectivity, and power consumption validation',
      'Field simulation testing for your specific deployment context',
      'Certification support (CE, FCC, BIS) and documentation',
    ],
  },
  {
    num:'05', title:'Deliver & Support',
    desc:'We hand over a production-ready product with complete documentation, DFM-ready manufacturing files, and a productisation roadmap. Then we stay engaged — supporting your launch, monitoring field performance, and iterating as your product evolves.',
    points:[
      'Complete hardware and software IP transfer with documentation',
      'DFM review and contract manufacturer handover package',
      'Production flashing setup and quality test procedures',
      'Post-launch monitoring, iteration, and ongoing product support',
    ],
  },
];

const DELIVERABLES = [
  { title:'System Architecture Document', desc:'Full hardware and software architecture document covering every layer of your product — ready to hand off to manufacturing or investment discussions.' },
  { title:'Hardware Design Package', desc:'Complete schematic files (EDA-native and PDF), PCB layout files (Gerber, ODB++, BOM, CPL), and 3D PCB render for mechanical fit verification.' },
  { title:'Prototype Units', desc:'Working prototype devices demonstrating all core functionality — assembled, tested, and validated against your product specification in our lab.' },
  { title:'Embedded Firmware', desc:'Production-grade firmware with HAL drivers, application logic, connectivity stacks, OTA update capability, and production flashing guide.' },
  { title:'Cloud Platform & API', desc:'Scalable IoT cloud backend with device management, data ingestion pipeline, REST/WebSocket API, and time-series storage.' },
  { title:'Web & Mobile Application', desc:'Custom IoT web dashboard (React.js) and mobile app (React Native or Flutter) for monitoring, control, and data visualisation.' },
  { title:'Test & Validation Reports', desc:'Documented results covering functional, environmental, connectivity, and power consumption testing against your specification.' },
  { title:'Productisation Roadmap', desc:'Costed roadmap from prototype to mass production including certification plan, tooling, supplier strategy, and manufacturing handover.' },
];

const CASE_STUDIES = [
  {
    tag: 'Agriculture',
    title: 'Smart Farm Monitoring Network',
    metric: '40% less water usage',
    desc: 'End-to-end IoT product for an agricultural cooperative in Karnataka — custom soil moisture and weather sensors, solar-powered nodes, LoRaWAN cloud integration, and a mobile dashboard used by 50+ farmers.',
  },
  {
    tag: 'Industrial',
    title: 'Machine Health Monitoring Platform',
    metric: '300+ devices deployed',
    desc: 'Vibration and temperature monitoring system for critical machinery in a Bangalore manufacturing facility — from PCB design through firmware, AWS cloud data pipeline, and a real-time alert dashboard.',
  },
  {
    tag: 'Healthcare',
    title: 'Connected Patient Vitals Monitor',
    metric: 'CE pre-compliance cleared',
    desc: 'Custom wearable hardware with BLE firmware, encrypted cloud integration, and a clinician-facing web application for a medical startup building continuous patient monitoring solutions.',
  },
];

const FAQS = [
  {
    q: 'What does end-to-end IoT product development involve?',
    a: 'End-to-end IoT product development covers the complete product lifecycle — from initial requirements and system architecture through hardware design, embedded firmware, cloud integration, web and mobile applications, testing, and production readiness. Zone-X handles every layer under one roof, eliminating the handoff risk that comes from using multiple vendors.',
  },
  {
    q: 'How long does it take to develop an IoT product from idea to prototype?',
    a: 'A focused MVP prototype typically takes 3 to 5 months depending on hardware complexity, connectivity requirements, and software scope. A production-ready product with full cloud integration and mobile app usually takes 6 to 12 months. We define a realistic timeline during the requirements phase before any development begins.',
  },
  {
    q: 'Can Zone-X take our concept all the way to manufacturing?',
    a: 'Yes. We support the full journey from concept to production — including DFM (Design for Manufacturability) review, contract manufacturer handover package, production flashing setup, and quality test procedures. We work with Indian and global contract manufacturers and prepare complete manufacturing-ready documentation.',
  },
  {
    q: 'Do you work with startups as well as established businesses?',
    a: 'Yes. We work with early-stage startups building their first IoT product, SMEs modernising existing systems, and enterprises developing new connected product lines. Our process is designed to accommodate projects at different stages of maturity — whether you are starting from a napkin sketch or an existing prototype that needs to be rebuilt properly.',
  },
  {
    q: 'Who owns the intellectual property developed during the project?',
    a: 'You do. All hardware designs, firmware source code, cloud architecture, and application code developed for your project are transferred to you in full. We provide source files, design files, and documentation with no lock-in or dependency on Zone-X after project completion.',
  },
];

function HeroVisual() {
  return (
    <img src="/visuals/services/Service 1.png" alt="IoT Product Engineering" style={{ width:'100%', display:'block' }} />
  );
}
export default function IoTProductPage() {
  const [heroRef, heroVisible] = useReveal();
  return (
    <>
      <Helmet>
        <title>IoT Product Development & Engineering Company India — Zone-X</title>
        <meta name="description" content="Zone-X is an IoT product development and engineering company in India — end-to-end hardware design, embedded firmware, cloud integration, and app development for connected products." />
        <link rel="canonical" href="https://zone-x.dev/iot-product-design-services" />
      </Helmet>

      <div ref={heroRef}>
        <ServiceHero
          tag="Services / IoT Product Engineering"
          breadcrumb="IoT Product Engineering"
          title="IoT Product Development &"
          titleAccent="Engineering"
          subtitle="Building a successful IoT product requires more than just hardware or software. It involves product strategy, electronics design, embedded development, connectivity, cloud infrastructure, user applications, and testing — all working together. Zone-X handles every layer, under one roof."
          geo="IoT product engineering and design services for businesses in Bangalore, Karnataka, and across India."
          color={COLOR}
          visible={heroVisible}
          visual={<HeroVisual />}
        />
      </div>

      <StatsRow />
      <IntroCard color={COLOR} />
      <ProcessSection steps={PROCESS} color={COLOR} />
      <WhatWeDeliver items={DELIVERABLES} color={COLOR} />
      <ServiceCaseStudies studies={CASE_STUDIES} color={COLOR} />
      <ServiceFAQ faqs={FAQS} color={COLOR} />
      <IndustriesSection />
      <WhyZoneX />
      <ServiceCTA />
      <Footer />
    </>
  );
}

function IntroCard({ color }) {
  const [ref, visible] = useReveal();
  return (
    <section style={{ padding:'0 0 clamp(32px,4vw,52px)' }}>
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
        <div ref={ref} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'clamp(24px,4vw,56px)', background:'rgba(255,255,255,0.8)', border:'1px solid rgba(10,10,20,0.08)', borderRadius:24, padding:'clamp(28px,4vw,52px)', opacity:visible?1:0, transform:visible?'none':'translateY(22px)', transition:'opacity 520ms, transform 520ms' }} className="iot-intro-grid">
          <div>
            <span style={{ fontFamily:"'Anton', sans-serif", fontSize:10.5, letterSpacing:'0.06em', color:color, textTransform:'uppercase' }}>WHAT WE DO</span>
            <h2 style={{ fontFamily:"'Anton',sans-serif", fontSize:'clamp(22px,3vw,36px)', lineHeight:1.1, marginTop:8, marginBottom:0, color:'var(--ink)' }}>
              One team for the{' '}
              <span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color:color }}>entire product journey</span>
            </h2>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <p style={{ fontSize:15, lineHeight:1.82, color:'var(--ink-soft)' }}>
              Most product companies piece together IoT development from multiple vendors — one for hardware, another for firmware, a third for the app. Every handoff is a risk: miscommunication, integration failures, schedule slippage, and no single point of accountability.
            </p>
            <p style={{ fontSize:15, lineHeight:1.82, color:'var(--ink-soft)' }}>
              Zone-X eliminates that problem. Our engineers work across every layer of your product under one roof — hardware, embedded firmware, cloud, and mobile — collaborating as one integrated team from day one. The result is a product that works as a system, not a collection of parts bolted together.
            </p>
          </div>
        </div>
      </div>
      <style>{`@media(max-width:760px){.iot-intro-grid{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
}
